/**
 * Pointer-driven move/resize for a timeline clip. Body-drag moves the clip
 * (preserving its length, snapping the start to words/seekbar); the right grip
 * resizes the out-time. Keeps a live `preview` window during the gesture and
 * commits once on release (onMove for a body drag, onResize for the right grip).
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { snapGlobal } from './items';

type Mode = 'move' | 'resize';

interface Options {
  pxPerMs: number;
  total: number;
  startMs: number;
  endMs: number | null;
  wordStarts: number[];
  playheadMs?: number;
  onMove: (globalStart: number) => void;
  onResize: (globalEnd: number) => void;
}

const MIN_CLIP_MS = 120;

export function useItemDrag({
  pxPerMs,
  total,
  startMs,
  endMs,
  wordStarts,
  playheadMs,
  onMove,
  onResize,
}: Options) {
  const [preview, setPreview] = useState<{ startMs: number; endMs: number | null } | null>(null);
  const drag = useRef<{ mode: Mode; clientX0: number; start0: number; end0: number | null } | null>(null);
  const previewRef = useRef<{ startMs: number; endMs: number | null } | null>(null);
  const opts = useRef({ pxPerMs, total, startMs, endMs, wordStarts, playheadMs, onMove, onResize });
  opts.current = { pxPerMs, total, startMs, endMs, wordStarts, playheadMs, onMove, onResize };

  useEffect(() => {
    const move = (ev: PointerEvent) => {
      const d = drag.current;
      if (!d) return;
      const { pxPerMs: ppm, total: tot, wordStarts: ws, playheadMs: ph } = opts.current;
      const snaps = ph != null ? [...ws, ph] : ws;
      const deltaMs = (ev.clientX - d.clientX0) / ppm;
      if (d.mode === 'move') {
        let s = Math.max(0, Math.min(tot, d.start0 + deltaMs));
        const snap = snapGlobal(s, snaps);
        if (snap != null) s = snap;
        const len = d.end0 != null ? d.end0 - d.start0 : null;
        const win = { startMs: s, endMs: len != null ? Math.min(tot, s + len) : null };
        previewRef.current = win;
        setPreview(win);
      } else {
        let e = Math.max(d.start0 + MIN_CLIP_MS, Math.min(tot, (d.end0 ?? d.start0) + deltaMs));
        const snap = snapGlobal(e, snaps);
        if (snap != null && snap > d.start0 + MIN_CLIP_MS) e = snap;
        const win = { startMs: d.start0, endMs: e };
        previewRef.current = win;
        setPreview(win);
      }
    };
    const up = () => {
      const d = drag.current;
      if (!d) return;
      drag.current = null;
      const win = previewRef.current;
      previewRef.current = null;
      setPreview(null);
      if (!win) return;
      if (d.mode === 'move') opts.current.onMove(win.startMs);
      else if (win.endMs != null) opts.current.onResize(win.endMs);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, []);

  const begin = useCallback(
    (mode: Mode) => (ev: React.PointerEvent) => {
      if (ev.button !== 0) return;
      ev.preventDefault();
      ev.stopPropagation();
      const o = opts.current;
      drag.current = { mode, clientX0: ev.clientX, start0: o.startMs, end0: o.endMs };
    },
    [],
  );

  return { preview, dragging: preview != null, onBodyDown: begin('move'), onResizeDown: begin('resize') };
}
